import axios from 'axios'

import timeoutPromise from './timeoutPromise'
import getTimeout     from './getTimeout'

const CLIENT_ERR = 'Une erreur est survenue lors de votre demande.';

const NO_RES_ERR = 'Votre demande n\'a pas pu être traitée. Il est possible que nos services soient momentanément indisponibles, veuillez patienter et réessayer.';

const ABORTED_ERR = 'Votre demande a dépassé le temps normal de traitement. Il est possible que nos services soient momentanément indisponibles, veuillez patienter et réessayer.';

const DEFAULT_404 = 'La ressource que vous avez demandé n\'existe pas.';

const SERVER_ERR = 'Une erreur est survenue lors du traitement de votre demande.';

const PERMS_ERR = 'Votre demande nécessite une authentification que vous n\'avez pas.';

const OFFLINE = 'Vous semblez hors ligne. Vérifiez votre connexion internet.';

const HTTP_METHODS =
[
	'GET', 'HEAD', 'POST', 'PUT', 'DELETE', 'CONNECT', 'OPTIONS', 'TRACE', 'PATCH'
]
.map(name => name.toLocaleLowerCase());

const ERRORS =
{
	'DENIED':
	{
		'default': 'Votre connexion a expirée, connectez-vous pour accéder à cette ressource.',

		'action': 'Votre connexion a expirée, connectez-vous pour pouvoir faire cette action.'
	},

	'FORBIDDEN':
	{
		'default': 'Votre n\'avez pas les droits nécessaires pour accéder à cette ressource.',

		'action': 'Votre n\'avez pas les droits nécessaires pour effectuer à cette action.'
	},

	'NOT_FOUND':
	{
		'default': 'Ressource introuvable.',

		'action': 'Données demandées introuvables.'
	}
};

/**
 * @typedef { Object<String, any> } RequestConfig
 *
 * @property { Object } config request config
 * @property { any } [config.navigation] react navigation prop
 * @property { AbortController } [config.controller] AbortController instance
 * @property { Boolean } [config.redirect=true] auto redirect on auth fail
 * @property { Boolean } [config.auth=true] add auth token in request
 * @property { Number } [config.min_req_time=600] minimum request duration (ms)
**/

/**
 * @description construct a request with its url
 *
 * @param { String } url req url (starting with / or uri with protocol like https:)
 *
 * @returns { Object<String, function> } chainable object with method (.post, etc)
**/
export default function request (url)
{
	const chainable =
	{
		config: function callConfiguration (props)
		{
			return configure(url, props);
		}
	};

	HTTP_METHODS.forEach(function (method)
	{
	/**
	 * @param { Object.<string, any> } body
	 *
	 * @returns { Promise<any> } .post, .get, etc with params
	**/
		chainable[method] = function callRequest (body)
		{
			return configure(url, { })[method](body);
		};
	});

	return chainable;
}

/**
 * @description configure the request
 *
 * @param { String } url passed url param
 * @param { RequestConfig } config
 *
 * @return { Object<String, function> } chainable object with method (.post, etc)
**/
function configure (url, config)
{
/** @type Object **/
	const chainable = { };

	HTTP_METHODS.forEach(function (method)
	{
	/**
	 * @param { Object<String, any> } body
	 *
	 * @returns { Promise<Object> } .post, .get, etc with params
	**/
		chainable[method] = function callRequest (body)
		{
			return makeRequest(url, method, config, body);
		};
	});

	return chainable;
}

/**
 * @param { String } url req url starting with /
 * @param { String } method HTTP method
 * @param { RequestConfig } config request configuration
 * @param { Object } params={ } request params
 *
 * @returns { Promise<Object> } reponse object
**/
async function makeRequest (url, method, config={ }, params)
{
	const { redirect=false, auth=true, min_req_time=400 } = config;

	let req_time = new Date();

/** @type { import('axios').AxiosRequestHeaders } **/
	const headers = { };

	let completed = false;

	let controller = config.controller || new AbortController();

	timeoutPromise(config.timeout).then(function ()
	{
		if (completed === false) controller.abort();
	});

	const cb = axios[method] || axios.get;

	try
	{
		const { data: response } = await cb(url, params,
		{
			headers: headers,
			signal: controller.signal,
			onUploadProgress: config.onUploadProgress
		});

		completed = true;

		if (controller?.signal?.aborted) throw 'ABORTED';
		await timeoutPromise(getTimeout(req_time, min_req_time));
		if (controller?.signal?.aborted) throw 'ABORTED';

		return response;
	}
	catch (e)
	{
		completed = true;

		if (e === 'ABORTED') throw getAbortedError();

		if (controller?.signal?.aborted) throw getAbortedError();
		await timeoutPromise(getTimeout(req_time, min_req_time));
		if (controller?.signal?.aborted) throw getAbortedError();

		delete window.__req_err_json;

		const update =
		{
			info: undefined,
			error: undefined,
			warning: undefined,
			fields: { },

			ERROR_CODE: undefined
		};

		if (!e.response && e.request)
		{
			appendErrorToWindow(e);

			if (navigator.onLine === false)
			{
				throw { error: OFFLINE };
			}

		// Pas de réponse du serveur
			if (e.request)
			{
				throw { error: NO_RES_ERR };
			}

		// Echec lors de la formation de la requête
			throw { error: CLIENT_ERR };
		}

	// Le seveur a répondu
		const response = e.response.data || '';
		const status_code = e.response.status || 500;

		['info', 'warning', 'error', 'ERROR_CODE']
		.forEach(field => update[field] = response[field]);

		if (typeof response.fields === 'object')
		{
			try
			{
				if (Object.keys(response.fields).length)
				{
					update.fields = response.fields;
				}
			}
			catch (e)
			{
				console.error(e);

				update.fields = { };
			}
		}

		if (!update.error)
		{
			appendErrorToWindow(e);

			update.error = SERVER_ERR;
		}

		if (status_code === 404 && !response.error)
		{
			update.ERROR_CODE = response.ERROR_CODE || 'NOT_FOUND';

			const error_msgs = ERRORS[update.ERROR_CODE] || { };

			update.error = error_msgs[config.keyword || 'default'] || DEFAULT_404;
		}

		if (config.throw_response)
		{
			update.response = response;
		}

		if (Array.isArray(response.errors))
		{
			update.errors = response.errors;
		}

		const not_auth = status_code === 401 && response.error === 'Unauthorized';
		const forbidden = status_code === 403 && response.error === 'Forbidden';

		if (not_auth || forbidden)
		{
			if (redirect)
			{
				return document.location.href = '/login';
			}

			update.ERROR_CODE = not_auth ? 'DENIED' : 'FORBIDDEN';

			const error_msgs = ERRORS[update.ERROR_CODE] || { };

			update.error = error_msgs[config.keyword || 'default'] || PERMS_ERR;
		}

		throw update;
	}
}

function appendErrorToWindow (e)
{
	if (typeof e.toJSON === 'function')
	{
		try
		{
			const err_json = e.toJSON();

			window.__req_err_json = window.JSON.stringify(err_json);
		}
		catch (e)
		{
			window.__req_err_json = undefined;
		}
	}
}

function getAbortedError ()
{
	if (navigator.onLine === false)
	{
		return { error: OFFLINE };
	}

// Pas de réponse du serveur
	return { error: ABORTED_ERR };
}